import { cn } from '@/lib/utils';
import { presentStatus, toneClasses, toneDotClasses, type StatusPresentation } from '@/lib/status';

interface StatusBadgeProps {
  /** One of the maps in `lib/status.ts` — LOAN_STATUS, KYC_STATUS, and so on. */
  map: Record<string, StatusPresentation>;
  status: string | null | undefined;
  className?: string;
  showDot?: boolean;
}

/**
 * The single way a status is rendered anywhere in the application.
 *
 * The coloured dot is not decoration: colour alone is not an accessible signal,
 * and the dot plus the always-present text label means the badge still reads
 * correctly to someone who cannot distinguish the tones.
 */
export function StatusBadge({ map, status, className, showDot = true }: StatusBadgeProps) {
  const presentation = presentStatus(map, status);

  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset',
        toneClasses(presentation.tone),
        className,
      )}
      title={presentation.description}
    >
      {showDot && (
        <span
          aria-hidden="true"
          className={cn('h-1.5 w-1.5 rounded-full', toneDotClasses(presentation.tone))}
        />
      )}
      {presentation.label}
    </span>
  );
}
